import axios from 'axios';
import { Dispatch } from 'redux';
import { TourDetails, City } from '@/data/types'; // Adjust the path as needed
import api from './api';
import { RootState } from "@/app/lib/store";

// Action Types
const GET_FEATURED_TOURS = '[TOURS] GET FEATURED TOURS';
const GET_FEATURED_TOURS_FAILURE = '[TOURS] GET FEATURED TOURS FAILURE';
const GET_TOURS = '[TOURS] GET TOURS';
const GET_TOURS_REQUEST = '[TOURS] GET TOURS REQUEST';
const GET_TOURS_FAILURE = '[TOURS] GET TOURS FAILURE';
const SET_PAGE_LIST_TOURS = '[TOURS] SET PAGE LIST TOURS';
const SET_PAGE_LIST_TOURS_FAILURE = '[TOURS] SET PAGE LIST TOURS FAILURE';
const SET_FILTERS = '[TOURS] SET FILTERS';
const SET_FILTER = '[TOURS] SET FILTER';
const SET_FAVOURITE_TOUR = '[TOURS] SET FAVOURITE TOUR';
const SET_FAVOURITE_TOUR_FAILURE = '[TOURS] SET FAVOURITE TOUR FAILURE';
const GET_FAVOURITE_TOURS = '[TOURS] GET FAVOURITE TOURS';
const GET_FAVOURITE_TOURS_FAILURE = '[TOURS] GET FAVOURITE TOURS FAILURE';
const GET_TOURS_BY_COUNTRY_REQUEST = '[TOURS] GET TOURS BY COUNTRY REQUEST';
const GET_TOURS_BY_COUNTRY = '[TOURS] GET TOURS BY COUNTRY';
const GET_TOURS_BY_COUNTRY_FAILURE = '[TOURS] GET TOURS BY COUNTRY FAILURE';
const RESET_TOURS_REQUEST = '[TOURS] RESET TOURS REQUEST';
const RESET_TOURS_SUCCESS = '[TOURS] RESET TOURS SUCCESS';
const RESET_TOURS_FAILURE = '[TOURS] RESET TOURS FAILURE';

// Action Interfaces
interface ResetToursRequestAction {
  type: typeof RESET_TOURS_REQUEST;
}

interface ResetToursSuccessAction {
  type: typeof RESET_TOURS_SUCCESS;
}

interface ResetToursFailureAction {
  type: typeof RESET_TOURS_FAILURE;
  payload: string; // error message
}

interface GetFeaturedToursAction {
  type: typeof GET_FEATURED_TOURS;
  payload: {
    featuredTours: TourDetails[];
    cities: City[];
  };
}

interface GetFeaturedToursFailureAction {
  type: typeof GET_FEATURED_TOURS_FAILURE;
  payload: string; // Error message
}

interface GetToursRequestAction {
  type: typeof GET_TOURS_REQUEST;
}

interface GetToursAction {
  type: typeof GET_TOURS;
  payload: {
    tours: TourDetails[];
    totalItems: number;
    totalPages: number;
    currentPage: number;
  };
}

interface GetToursFailureAction {
  type: typeof GET_TOURS_FAILURE;
  payload: string; // Error message
}

interface SetPageListToursAction {
  type: typeof SET_PAGE_LIST_TOURS;
  payload: number;
}

interface SetPageListToursFailureAction {
  type: typeof SET_PAGE_LIST_TOURS_FAILURE;
  payload: string; // Error message
}

interface SetFiltersAction {
  type: typeof SET_FILTERS;
  payload: {
    categories: string[];
    people: number;
    minPrice: number;
    maxPrice: number;
    city: number | null;
    startAt: string[];
    duration: string[];
    specialFilters: string[];
  };
}

interface SetFilterAction {
  type: typeof SET_FILTER;
  payload: {
    key: keyof typeof initialState.filters; // This ensures only valid filter keys can be used
    value: any; // The new value for the filter
  };
}

interface SetFavouriteTourAction {
  type: typeof SET_FAVOURITE_TOUR;
  payload: string; // Message
}

interface SetFavouriteTourFailureAction {
  type: typeof SET_FAVOURITE_TOUR_FAILURE;
  payload: string; // Error message
}

interface GetFavouriteToursAction {
  type: typeof GET_FAVOURITE_TOURS;
  payload: {
    currentPage: number;
    totalItems: number;
    totalPages: number;
    tours: TourDetails[];
  };
}

interface GetFavouriteToursFailureAction {
  type: typeof GET_FAVOURITE_TOURS_FAILURE;
  payload: string; // Error message
}

interface GetToursByCountryRequestAction {
  type: typeof GET_TOURS_BY_COUNTRY_REQUEST;
}

interface GetToursByCountryAction {
  type: typeof GET_TOURS_BY_COUNTRY;
  payload: TourDetails[];
}

interface GetToursByCountryFailureAction {
  type: typeof GET_TOURS_BY_COUNTRY_FAILURE;
  payload: string; // error
}


type ToursActions =
  | GetFeaturedToursAction
  | GetFeaturedToursFailureAction
  | GetToursRequestAction
  | GetToursAction
  | GetToursFailureAction
  | SetPageListToursAction
  | SetPageListToursFailureAction
  | SetFiltersAction
  | SetFilterAction
  | SetFavouriteTourAction
  | GetFavouriteToursAction
  | GetFavouriteToursFailureAction
  | GetToursByCountryRequestAction
  | GetToursByCountryAction
  | GetToursByCountryFailureAction
  | ResetToursRequestAction
  | ResetToursSuccessAction
  | ResetToursFailureAction;

export const resetTours = () => async (dispatch: Dispatch<ToursActions>) => {
  try {
    dispatch({ type: RESET_TOURS_REQUEST });

    // Simulate a delay or API call (if needed)
    await new Promise(resolve => setTimeout(resolve, 200)); // Optional

    dispatch({ type: RESET_TOURS_SUCCESS });
  } catch (error: any) {
    dispatch({
      type: RESET_TOURS_FAILURE,
      payload: error.message || 'Error resetting tours',
    });
  }
};


export const getToursByCountry = (slug: string, language: string = 'en_US') =>
  async (dispatch: Dispatch<ToursActions>) => {
    try {
      dispatch({ type: GET_TOURS_BY_COUNTRY_REQUEST });

      // Récupère l'utilisateur connecté depuis Redux
      const connectedUser = JSON.parse(localStorage.getItem('user') || '');

      const response = await api.get(`/api/tours-by-country/${slug}`, {
        params: { language },
        headers: connectedUser?.user.uuid
          ? {
              'user-info': JSON.stringify({
                user: { uuid: connectedUser.user.uuid },
              }),
            }
          : {},
      });


      if (response.data && response.data.tours) {
        dispatch({
          type: GET_TOURS_BY_COUNTRY,
          payload: response.data.tours,
        });
      } else {
        dispatch({
          type: GET_TOURS_BY_COUNTRY_FAILURE,
          payload: 'No tours found for this country.',
        });
      }
    } catch (error: any) {
      console.error('Error fetching tours by country:', error);
      dispatch({
        type: GET_TOURS_BY_COUNTRY_FAILURE,
        payload: error.message || 'Error fetching tours by country',
      });
    }
};

// Fetch Featured Tours
export const getFeaturedTours = (language: string) => async (dispatch: Dispatch<GetFeaturedToursAction | GetFeaturedToursFailureAction>) => {
  try {
    const response = await api.get(`/api/featured-tours`, {
      params: { language }
    });
    if (response.data) {
      dispatch({
        type: GET_FEATURED_TOURS,
        payload: response.data,
      });
    } else {
      dispatch({
        type: GET_FEATURED_TOURS_FAILURE,
        payload: 'No featured tours found.',
      });
    }
  } catch (error: any) {
    console.error('Error fetching featured tours:', error);
    dispatch({
      type: GET_FEATURED_TOURS_FAILURE,
      payload: error.message || 'Error fetching featured tours',
    });
  }
};

// Sync setter for server-side hydration of the tours list
export const setTours = (payload: {
  tours: any[];
  totalItems: number;
  totalPages: number;
  currentPage: number;
}) => ({
  type: GET_TOURS as typeof GET_TOURS,
  payload,
});

// Fetch Tours with Pagination and Filters
// Monotonic counter incremented on every `getTours` call. Each
// invocation captures its own id and ignores the response if a NEWER
// call has fired in the meantime. Required because navigations between
// /city/<slug> routes can trigger overlapping fetches (initial-load
// fetch for the old city still in flight when the new city's fetch
// fires) — without this guard the old response races ahead and
// overwrites the new city's tours in Redux. Symptom: user navigates
// from Barcelona → Rennes and still sees Barcelona's tour cards.
let getToursRequestId = 0;

export const getTours = (
  page: number,
  size: number,
  categories: string[] = [],
  people: number = 2,
  minPrice?: number,
  maxPrice?: number,
  city?: number | null,
  language: string = 'en_US',
  startAt?: string[],
  duration?: string[],
  specialFilters?: string[]
) => async (dispatch: Dispatch<ToursActions>) => {
  const myRequestId = ++getToursRequestId;

  try {
    dispatch({ type: GET_TOURS_REQUEST });

    const token = localStorage.getItem('token');
    const user = localStorage.getItem('user');
    const headers = token ? { Authorization: `Bearer ${token}`, 'User-Info': user } : { 'User-Info': user };

    const response = await api.get(`/api/tours`, {
      params: { page, size, categories, people, minPrice, maxPrice, city, language, startAt, duration, specialFilters },
      headers: headers,
    });

    // A newer call has been made — drop this stale response so it
    // doesn't overwrite the current city's tour list.
    if (myRequestId !== getToursRequestId) return;

    if (response.data) {
      dispatch({
        type: GET_TOURS,
        payload: {
          tours: response.data.tours,
          totalItems: response.data.totalItems,
          totalPages: response.data.totalPages,
          currentPage: response.data.currentPage,
        },
      });
    } else {
      dispatch({
        type: GET_TOURS_FAILURE,
        payload: 'No tours found.',
      });
    }
  } catch (error: any) {
    if (myRequestId !== getToursRequestId) return; // dropped — see above
    console.error('Error fetching tours:', error);
    dispatch({
      type: GET_TOURS_FAILURE,
      payload: error.message || 'Error fetching tours',
    });
  }
};


// Set Favourite Tour
export const setFavouriteTour = (uuid: string, userUuid: string) => async (dispatch: Dispatch<SetFavouriteTourAction | SetFavouriteTourFailureAction>) => {
  try {
    const token = localStorage.getItem('token'); // Assuming token is stored in localStorage
    const response = await api.put(`/api/tour/${uuid}/user-favourite/${userUuid}`, null, {
      headers: {
        Authorization: `Bearer ${token}`,
      }
    });
    dispatch({
      type: SET_FAVOURITE_TOUR,
      payload: response.data.message,
    });
  } catch (error: any) {
    console.error('Error setting favourite tour:', error);
    dispatch({
      type: SET_FAVOURITE_TOUR_FAILURE,
      payload: error.message || 'Error setting favourite tour',
    });
  }
};

// Get Favourite Tours
export const getFavouriteTours = (userUuid: string, page: number) => async (dispatch: Dispatch<GetFavouriteToursAction | GetFavouriteToursFailureAction>) => {
  try {
    const token = localStorage.getItem('token'); // Assuming token is stored in localStorage
    const response = await api.get(`/api/user/${userUuid}/favourite-tours`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      params: {
        page: page
      }
    });
    dispatch({
      type: GET_FAVOURITE_TOURS,
      payload: {
        currentPage: response.data.favouriteTours.currentPage,
        totalItems: response.data.favouriteTours.totalItems,
        totalPages: response.data.favouriteTours.totalPages,
        tours: response.data.favouriteTours.tours,
      },
    });
  } catch (error: any) {
    console.error('Error fetching favourite tours:', error);
    dispatch({
      type: GET_FAVOURITE_TOURS_FAILURE,
      payload: error.message || 'Error fetching favourite tours',
    });
  }
};

// Set Page List Tours
export const setPageListTours = (page: number) => (dispatch: Dispatch<SetPageListToursAction | SetPageListToursFailureAction>) => {
  try {
    dispatch({
      type: SET_PAGE_LIST_TOURS,
      payload: page,
    });
  } catch (error: any) {
    dispatch({
      type: SET_PAGE_LIST_TOURS_FAILURE,
      payload: error.message || 'Error setting page list tours',
    });
  }
};

// Set Filters
export const setFilters = (categories: string[], people: number, minPrice: number, maxPrice: number, city: number | null, startAt: string[], duration: string[], specialFilters: string[]): SetFiltersAction => ({
  type: SET_FILTERS,
  payload: { categories, people, minPrice, maxPrice, city, startAt, duration, specialFilters },
});

export const setFilter = (key: keyof typeof initialState.filters, value: any): SetFilterAction => ({
  type: SET_FILTER,
  payload: { key, value },
});


// Initial State
const initialState = {
  featured: [] as TourDetails[],
  cities: [] as City[],
  tours: [] as TourDetails[],
  filters: {
    categories: [] as string[],
    people: 2,
    minPrice: 0,
    maxPrice: 1000,
    city: null,
    startAt: [] as string[],
    duration: [] as string[],
    specialFilters: [] as string[],
    // Client-side filter (city page): tour must have at least one schedule
    // whose `minStart` is ≤ startBefore and/or ≥ startAfter. "HH:mm" format
    // (empty string = unset). Not sent to the backend — applied in
    // SectionGridFilterCard against the already-loaded tour list.
    startBefore: "" as string,
    startAfter: "" as string,
    // Client-side filter (city page): tour must offer at least one of the
    // selected language codes (e.g. ["fr_FR", "en_US"]). Empty array = no
    // constraint. Read from each tour's `tourLanguages[].languageInfo.code`.
    languages: [] as string[],
  },
  totalItems: 0,
  totalPages: 0,
  currentPage: 1,
  error: null as string | null,
  resetError: null as string | null, // 👈 optional
  loading: false,
  favouriteTours: {
    currentPage: 1,
    totalItems: 0,
    totalPages: 0,
    tours: [] as TourDetails[],
  },
  byCountry: [] as TourDetails[],
  loadingByCountry: false,
};

// Reducer
const toursReducer = (state = initialState, action: ToursActions) => {
  switch (action.type) {
    case RESET_TOURS_REQUEST:
      return {
        ...state,
        loading: true,
        resetError: null,
      };

    case RESET_TOURS_SUCCESS:
      return {
        ...state,
        tours: [],
        totalItems: 0,
        totalPages: 0,
        currentPage: 1,
        loading: false,
        resetError: null,
      };

    case RESET_TOURS_FAILURE:
      return {
        ...state,
        loading: false,
        resetError: action.payload,
      };
    case GET_FEATURED_TOURS:
      return {
        ...state,
        featured: action.payload.featuredTours,
        cities: action.payload.cities,
        error: null,
    };
    case GET_FEATURED_TOURS_FAILURE:
      return {
        ...state,
        error: action.payload,
    };
    case GET_TOURS_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
      };
    case GET_TOURS:
      return {
        ...state,
        tours: action.payload.tours,
        totalItems: action.payload.totalItems,
        totalPages: action.payload.totalPages,
        currentPage: action.payload.currentPage,
        error: null,
        loading: false,
    };
    case GET_TOURS_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload,
    };
    case SET_PAGE_LIST_TOURS:
      return {
        ...state,
        currentPage: action.payload,
        error: null,
    };
    case SET_PAGE_LIST_TOURS_FAILURE:
      return {
        ...state,
        error: action.payload,
    };
    case SET_FILTER:{
      // Prevent infinite loops by checking if the value changed
      if (state.filters[action.payload.key] === action.payload.value) {
        return state;
      }

      return {
        ...state,
        filters: {
          ...state.filters,
          [action.payload.key]: action.payload.value,
        },
      };
    }
    case SET_FILTERS:
      return {
        ...state,
        filters: {
          categories: action.payload.categories,
          people: action.payload.people,
          minPrice: action.payload.minPrice,
          maxPrice: action.payload.maxPrice,
          city: action.payload.city,
          startAt: action.payload.startAt,
          duration: action.payload.duration,
          specialFilters: action.payload.specialFilters,
        },
        error: null,
      };
    case SET_FAVOURITE_TOUR:
      return {
        ...state,
        // Handle favourite tour logic here if needed
      };
    case GET_FAVOURITE_TOURS:
      return {
        ...state,
        favouriteTours: action.payload,
        error: null,
      };
    case GET_FAVOURITE_TOURS_FAILURE:
      return {
        ...state,
        error: action.payload,
    };
    case GET_TOURS_BY_COUNTRY_REQUEST:
      return {
        ...state,
        loadingByCountry: true,
        error: null,
      };
    case GET_TOURS_BY_COUNTRY:
      return {
        ...state,
        byCountry: action.payload,
        loadingByCountry: false,
        error: null,
      };
    case GET_TOURS_BY_COUNTRY_FAILURE:
      return {
        ...state,
        byCountry: [],
        loadingByCountry: false,
        error: action.payload,
      };
    default:
      return state;
  }
};

export default toursReducer;
